import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Pagination, CircularProgress, Chip, Typography, ToggleButton, ToggleButtonGroup, Alert,
} from '@mui/material';
import { fetchCustomerUnlocks } from '../api';
import { formatDate, headerCell, surface } from '../format';

const UnlocksTab = ({ customerId }) => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [unlockType, setUnlockType] = useState('');

    useEffect(() => {
        setPage(1);
    }, [unlockType]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await fetchCustomerUnlocks(customerId, {
                    page,
                    limit: 10,
                    unlock_type: unlockType || undefined,
                });
                if (cancelled) return;
                setRows(res.data.data || []);
                setTotalPages(res.data.totalPages || 1);
                setTotal(res.data.total || 0);
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.message || 'Failed to load unlocks');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [customerId, page, unlockType]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                    {total} unlock{total === 1 ? '' : 's'}
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={unlockType}
                    onChange={(_, v) => { if (v !== null) setUnlockType(v); }}
                >
                    <ToggleButton value="" sx={{ textTransform: 'none', px: 1.5 }}>All</ToggleButton>
                    <ToggleButton value="email" sx={{ textTransform: 'none', px: 1.5 }}>Email</ToggleButton>
                    <ToggleButton value="download" sx={{ textTransform: 'none', px: 1.5 }}>Download</ToggleButton>
                    <ToggleButton value="phone" sx={{ textTransform: 'none', px: 1.5 }}>Phone</ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <Paper elevation={0} sx={surface}>
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {['Company', 'Contact', 'Title', 'Event', 'Type', 'Credits', 'Date'].map((h) => (
                                        <TableCell key={h} sx={headerCell}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#6b7280' }}>
                                            No unlocks yet.
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map((row) => {
                                    const lead = row.lead_details || {};
                                    const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ');
                                    return (
                                        <TableRow key={row.id} hover>
                                            <TableCell>{lead.company_name || '—'}</TableCell>
                                            <TableCell>
                                                <Box>
                                                    {name || '—'}
                                                    {lead.email && (
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                            {lead.email}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{lead.title || '—'}</TableCell>
                                            <TableCell>{lead.event_name || '—'}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={row.unlock_type} sx={{ bgcolor: '#f3f4f6', textTransform: 'capitalize' }} />
                                            </TableCell>
                                            <TableCell>{row.credits_deducted}</TableCell>
                                            <TableCell sx={{ color: '#6b7280' }}>{formatDate(row.createdAt)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" />
                </Box>
            )}
        </Box>
    );
};

export default UnlocksTab;
